// Shared while a screen subscribes. Keys include authentication and the full
// query scope; no sale data is persisted or reused after the last unmount.
const resources = new Map();

export const getPendingPurchasesResource = (key, load, isCurrent) => {
  if (resources.has(key)) return resources.get(key);
  let snapshot = { pending: [], loading: true, error: null };
  const listeners = new Set();
  let timer = null;
  let running = false;
  let queued = false;
  let generation = 0;
  let waiters = [];
  const publish = (next) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  const drain = async () => {
    timer = null;
    if (running || !listeners.size) return;
    running = true;
    while (queued && listeners.size) {
      queued = false;
      const version = generation;
      const currentWaiters = waiters;
      waiters = [];
      let success = false;
      try {
        if (isCurrent()) {
          const pending = await load();
          if (version === generation && listeners.size && isCurrent()) {
            publish({ pending, loading: false, error: null });
            success = true;
          }
        }
      } catch (error) {
        if (version === generation && listeners.size && isCurrent()) {
          publish({ ...snapshot, loading: false, error });
        }
      }
      currentWaiters.forEach((resolve) => resolve(success));
    }
    running = false;
  };
  const refresh = ({ immediate = false } = {}) => {
    if (!listeners.size) return Promise.resolve(false);
    queued = true;
    const done = new Promise((resolve) => { waiters.push(resolve); });
    if (!running && timer === null) timer = setTimeout(drain, immediate ? 0 : 250);
    return done;
  };
  const resource = {
    getSnapshot: () => snapshot,
    refresh,
    subscribe: (listener) => {
      const first = listeners.size === 0;
      listeners.add(listener);
      resources.set(key, resource);
      if (first) refresh({ immediate: true });
      return () => {
        listeners.delete(listener);
        if (listeners.size) return;
        generation += 1;
        queued = false;
        clearTimeout(timer);
        timer = null;
        waiters.splice(0).forEach((resolve) => resolve(false));
        if (resources.get(key) === resource) resources.delete(key);
      };
    },
  };
  resources.set(key, resource);
  return resource;
};
